import React, { useState, useMemo } from "react";
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  MarkEmailRead as EmailIcon,
  ThumbUp as ApprovalIcon,
} from "@mui/icons-material";
import { useQuery } from "@apollo/client";
import { useUserGroups } from "../../hooks/useUserGroups";
import {
  GET_MY_NOTIFICATIONS,
  GET_PENDING_SUBMISSIONS_COUNT,
  GET_ALL_PENDING_SUBMISSIONS_COUNT,
} from "../../graphql/operations";
import { useNavigate } from "react-router-dom";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  formId?: string;
  submissionId?: string;
  approvedBy: string;
  dueDate?: string;
  isRead: boolean;
  createdAt: string;
  username: string;
}

const NotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { isAdmin } = useUserGroups();
  const navigate = useNavigate();

  const { data: notificationsData, refetch } = useQuery(GET_MY_NOTIFICATIONS, {
    pollInterval: 300000,
  });

  const notifications = notificationsData?.getMyNotifications?.items || [];
  const unreadCount = notifications.filter((n: any) => !n.isRead).length;

  const countQuery = isAdmin
    ? GET_ALL_PENDING_SUBMISSIONS_COUNT
    : GET_PENDING_SUBMISSIONS_COUNT;

  const { data: countData } = useQuery(countQuery, {
    pollInterval: 300000,
    skip: notifications.length === 0,
  });

  const pendingCount =
    countData?.getPendingSubmissionsCount ||
    countData?.getAllPendingSubmissionsCount ||
    0;

  // Categorize notifications for admin view
  const categorizedNotifications = useMemo(() => {
    if (!isAdmin) return { pendingSubmissions: notifications, approvals: [] };

    const pendingSubmissions = notifications.filter(
      (notification: Notification) => !notification.submissionId
    );

    const approvals = notifications.filter(
      (notification: Notification) =>
        notification.submissionId && !notification.approvedBy
    );

    return { pendingSubmissions, approvals };
  }, [notifications, isAdmin]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (notification.submissionId) {
      navigate(`/dashboard/submissions/${notification.submissionId}`);
    } else if (notification.formId) {
      navigate(`/dashboard/submissions/new/${notification.formId}`);
    }

    handleClose();
    refetch();
  };

  const handleApprovalClick = (notification: Notification) => {
    // Navigate to submissions list page to approve the specific submission
    if (notification.submissionId) {
      navigate(`/dashboard/submissions?highlight=${notification.submissionId}`);
    } else {
      navigate("/dashboard/submissions");
    }
    handleClose();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "FORM_ASSIGNMENT":
        return <AssignmentIcon color="primary" />;
      case "SUBMISSION_DUE":
      case "SUBMISSION_OVERDUE":
        return <WarningIcon color="warning" />;
      case "SUBMISSION_APPROVED":
        return <CheckCircleIcon color="success" />;
      case "SUBMISSION_REJECTED":
        return <CancelIcon color="error" />;
      case "SUBMISSION_SUBMITTED":
        return <ApprovalIcon color="info" />;
      default:
        return <EmailIcon />;
    }
  };

  const isOverdue = (dueDate: string) => {
    return new Date(dueDate) < new Date();
  };

  const renderNotificationItem = (
    notification: Notification,
    onClick: (notification: Notification) => void
  ) => (
    <MenuItem
      key={notification.id}
      onClick={() => onClick(notification)}
      sx={{
        backgroundColor: notification.isRead ? "transparent" : "action.hover",
        borderLeft: notification.isRead ? "none" : "4px solid",
        borderLeftColor: "primary.main",
      }}
    >
      <ListItemIcon>{getNotificationIcon(notification.type)}</ListItemIcon>
      <Box sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 0.5,
          }}
        >
          <Typography variant="subtitle2" component="span">
            {notification.title}
          </Typography>
          {notification.dueDate && isOverdue(notification.dueDate) && (
            <Chip label="Overdue" color="error" size="small" />
          )}
        </Box>
        {isAdmin && (
          <Typography variant="body2" color="textSecondary" component="div">
            To: {notification.username}
          </Typography>
        )}
        {!isAdmin && (
          <Typography variant="body2" color="textSecondary" component="div">
            {notification.message}
          </Typography>
        )}
        {notification.dueDate && (
          <Typography variant="caption" color="textSecondary" component="div">
            Due: {new Date(notification.dueDate).toLocaleDateString()}
          </Typography>
        )}
      </Box>
    </MenuItem>
  );

  // Create menu content
  const menuContent = (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      PaperProps={{
        sx: { width: 400, maxHeight: 500 },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" component="div">
          Notifications {unreadCount > 0 && `(${unreadCount} new)`}
        </Typography>
        <Typography variant="body2" color="textSecondary" component="div">
          {isAdmin ? "All pending submissions" : "Your pending submissions"}:{" "}
          {pendingCount}
        </Typography>
        {isAdmin && (
          <Typography variant="body2" color="textSecondary" component="div">
            Pending approvals: {categorizedNotifications.approvals.length}
          </Typography>
        )}
      </Box>

      <Divider />

      <List sx={{ p: 0 }}>
        {notifications.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="No notifications"
              secondary="You're all caught up!"
            />
          </ListItem>
        ) : (
          <>
            {/* For non-admin users, show all notifications together */}
            {!isAdmin &&
              notifications
                .slice(0, 10)
                .map((notification: Notification) =>
                  renderNotificationItem(notification, handleNotificationClick)
                )}

            {/* For admin users, show categorized notifications */}
            {isAdmin && (
              <>
                {/* Pending Submissions Section */}
                {categorizedNotifications.pendingSubmissions.length > 0 && (
                  <>
                    <Box sx={{ px: 2, py: 1, backgroundColor: "grey.50" }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        PENDING SUBMISSIONS
                      </Typography>
                    </Box>
                    {categorizedNotifications.pendingSubmissions
                      .slice(0, 5)
                      .map((notification: Notification) =>
                        renderNotificationItem(
                          notification,
                          handleNotificationClick
                        )
                      )}
                  </>
                )}

                {/* Approvals Section */}
                {categorizedNotifications.approvals.length > 0 && (
                  <>
                    <Divider />
                    <Box sx={{ px: 2, py: 1, backgroundColor: "grey.50" }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        APPROVALS NEEDED
                      </Typography>
                    </Box>
                    {categorizedNotifications.approvals
                      .slice(0, 5)
                      .map((notification: Notification) =>
                        renderNotificationItem(
                          notification,
                          handleApprovalClick
                        )
                      )}
                  </>
                )}

                {/* Show message if no notifications in either category */}
                {categorizedNotifications.pendingSubmissions.length === 0 &&
                  categorizedNotifications.approvals.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary="No notifications"
                        secondary="You're all caught up!"
                      />
                    </ListItem>
                  )}
              </>
            )}
          </>
        )}
      </List>

      {notifications.length > 10 && (
        <>
          <Divider />
          <Box sx={{ p: 1, textAlign: "center" }}>
            <Button size="small" onClick={() => navigate("/notifications")}>
              View All Notifications
            </Button>
          </Box>
        </>
      )}
    </Menu>
  );

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{ position: "relative" }}
      >
        <Badge badgeContent={unreadCount} color="error" overlap="circular">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      {menuContent}
    </>
  );
};

export default NotificationBell;
